import type { Erc20Transfer } from '../../../types/erc20'

/** 別の ERC20 トークンの移動を鏡写しにして Transfer event を発行するトークンの定義 */
type MirrorErc20Token = {
  /** 対象チェーンの chain id */
  readonly chainId: number
  /** 除外する側 (鏡像) のトークンアドレス */
  readonly mirrorAddress: string
  /** 残す側 (正) のトークンアドレス */
  readonly canonicalAddress: string
}

/**
 * 鏡像の Transfer event を発行するトークンの一覧
 * - HyperEVM の wstHYPE (Staked HYPE Shares) は独自の残高を持たず、stHYPE の shares をそのまま見せるトークンである。
 *   wstHYPE の transfer は内部で `stHYPE.transferShares` を呼び、stHYPE の Transfer event を発行した直後に wstHYPE 自身の Transfer event を発行するため、同じ移動が 2 つの transfer として現れる。
 *   stHYPE の Transfer event は stHYPE を直接動かした場合にも発行されるので、stHYPE を正とし、wstHYPE の鏡像を除外する。
 */
export const mirrorErc20Tokens: readonly MirrorErc20Token[] = [
  {
    chainId: 999,
    mirrorAddress: '0x94e8396e0869c9f2200760af0621afd240e1cf38',
    canonicalAddress: '0xffaa4a3d97fe9107cef8a3f48c069f577ff76cc1',
  },
]

/**
 * 鏡像の ERC20 transfer を除外する
 * - 鏡像トークンの transfer の直前の log が、from / to が同じ正のトークンの transfer である場合のみ除外する
 * - 対になる正のトークンの transfer が無い場合は、取りこぼしを避けるため除外しない
 * @param chainId - transaction の chain id
 * @param transfers - transaction の ERC20 transfer 一覧
 * @returns 鏡像を除いた ERC20 transfer 一覧 (元の順序を維持する)
 */
export const filterMirrorErc20Transfers = <T extends Erc20Transfer>({
  chainId,
  transfers,
}: {
  chainId: number
  transfers: readonly T[]
}): T[] => {
  const mirrors = mirrorErc20Tokens.filter(
    (mirror) => mirror.chainId === chainId,
  )
  if (mirrors.length === 0) {
    return [...transfers]
  }

  const results: T[] = []
  for (const transfer of transfers) {
    const mirror = mirrors.find(
      ({ mirrorAddress }) => mirrorAddress === transfer.address,
    )
    if (mirror === undefined) {
      results.push(transfer)
      continue
    }
    const hasCanonicalTransfer = transfers.some(
      (candidate) =>
        candidate.address === mirror.canonicalAddress &&
        candidate.logIndex === transfer.logIndex - 1 &&
        candidate.from === transfer.from &&
        candidate.to === transfer.to,
    )
    if (!hasCanonicalTransfer) {
      results.push(transfer)
    }
  }
  return results
}
